import { useMemo, useState } from 'react';
import type { GroupInfo } from '../../api';
import { groupOptions } from '../../devices/groupTree';

/** "최상위" 를 뜻하는 고르기 값(그룹 이동에서만 쓴다). */
export const ROOT_OPTION = '__root__';

/**
 * 그룹 고르기 + [확인]/[취소](설계서 §14.20) — 기기·ID 항목·그룹 이동과 등록 요청 수락이 함께 쓴다.
 * 목록은 트리 순서의 경로 이름("본사 › 개발팀").
 *
 * @param initial 처음 골라 둘 값(없으면 첫 항목)
 * @param allowRoot "최상위" 를 넣을지(그룹 이동)
 * @param disabledReason 고른 값이 안 되는 이유(없으면 null) — 확인 단추를 잠그고 이유를 보인다
 */
export function GroupPicker({
  groups,
  initial,
  allowRoot = false,
  confirmLabel = '옮기기',
  busy = false,
  disabledReason,
  onConfirm,
  onCancel,
}: {
  groups: readonly GroupInfo[];
  initial?: string;
  allowRoot?: boolean;
  confirmLabel?: string;
  busy?: boolean;
  disabledReason?: (value: string) => string | null;
  onConfirm: (value: string) => void;
  onCancel?: () => void;
}) {
  const options = useMemo(() => groupOptions(groups), [groups]);
  const [value, setValue] = useState(initial ?? (allowRoot ? ROOT_OPTION : options[0]?.id ?? ''));
  const reason = value ? disabledReason?.(value) ?? null : '그룹을 고르세요.';
  return (
    <span className="row gtree-picker" onClick={(e) => e.stopPropagation()} onDoubleClick={(e) => e.stopPropagation()}>
      <select value={value} onChange={(e) => setValue(e.target.value)} disabled={busy} aria-label="그룹 고르기">
        {allowRoot && <option value={ROOT_OPTION}>(최상위)</option>}
        {options.map((o) => (
          <option key={o.id} value={o.id}>
            {o.label}
          </option>
        ))}
      </select>
      <button onClick={() => onConfirm(value)} disabled={busy || !!reason} title={reason ?? undefined}>
        {busy ? '처리 중…' : confirmLabel}
      </button>
      {onCancel && (
        <button className="ghost" onClick={onCancel} disabled={busy}>
          취소
        </button>
      )}
      {reason && value && <span className="hint">{reason}</span>}
    </span>
  );
}
